import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import {
  LeadsFilters,
  parseQueryParamsForLeadsFilters,
} from "../../../../../models/Leads/leadsFilter";
import { SortType } from "../../../../../models/SortType";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import LeadTable from "../../components/leads/LeadsTable";
import { LeadsStore } from "../../../stores/LeadsStore";
import LeadDetailsPopup from "../../components/leads/LeadDetailsPopup";
import { Leads } from "../../../../../models/Leads/leads";
import DownloadIcon from "@mui/icons-material/Download";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";

const leadStore = new LeadsStore();

const LeadsListing: React.FC = () => {
  const theme = useTheme();
  const { userStore } = useStores();
  const { showError } = useNotification();

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialFilters: LeadsFilters = parseQueryParamsForLeadsFilters(
    null,
    searchParams,
  );

  const [filters, setFilters] = useState<LeadsFilters>(initialFilters);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);

  useEffect(() => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_LEADS])) {
      if (leadStore.sources.length === 0) {
        leadStore
          .getSources()
          .catch((err) =>
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            ),
          );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_LEADS])) {
      setIsLoading(true);
      leadStore
        .getLeads(filters)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value?.toString() || "";
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, navigate]);

  const handleSearchBar = async (searchText: string | undefined) => {
    if (searchText && searchText.length !== 0) {
      setSearchTerm(searchText);
    } else {
      setSearchTerm("");
      setFilters((prev: LeadsFilters) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    setFilters((prev: LeadsFilters) => ({
      ...prev,
      search_text: searchTerm,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setFilters((prev: LeadsFilters) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setFilters((prev: LeadsFilters) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setFilters((prev: LeadsFilters) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev: LeadsFilters) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleViewDetails = (lead: Leads) => {
    leadStore.setSelectedLeadDetails(lead);
    leadStore.openDetailsPopup();
  };

  const handleCloseDetails = () => {
    leadStore.closeDetailsPopup();
  };

  const handleDownload = () => {
    const headers = [
      "ID",
      "Kylas ID",
      "Name",
      "Email",
      "Mobile",
      "Source",
      "Lead Source",
      "Company Name",
      "Company Size",
      "Department",
      "Referral ID",
      "UTM Source",
      "UTM Medium",
      "UTM Campaign ID",
      "UTM Keyword",
      "UTM Term",
      "UTM Device",
      "UTM Location",
      "UTM Match Type",
      "Browser",
      "Timezone",
      "Device Type",
      "Landing Page URL",
      "Form Submission Page URL",
      "Referrer URL",
      "Created Date",
    ];

    const escapeCsv = (value: any) => {
      const cell = String(value ?? "").replace(/"/g, '""');
      return `"${cell}"`;
    };

    const rows = leadStore.leads.map((lead) => [
      lead.id,
      lead.kylas_lead_id || "",
      lead.name || "",
      lead.email_id || "",
      lead.mobile || "",
      lead.source || "",
      lead.lead_source || "",
      lead.company_name || "",
      lead.company_size || "",
      lead.department_name || "",
      lead.referral_id || "",
      lead.utm_source || "",
      lead.utm_medium || "",
      lead.utm_campaignid || "",
      lead.utm_keyword || "",
      lead.utm_term || "",
      lead.utm_device || "",
      lead.utm_loc_physical_ms || "",
      lead.utm_matchtype || "",
      lead.browser || "",
      lead.timezone || "",
      lead.submission_device_type || "",
      lead.landing_page_url || "",
      lead.form_submission_page_url || "",
      lead.referrer_url || "",
      lead.created_date ? formatDate(lead.created_date) : "",
    ]);

    const csvContent = [
      headers.map(escapeCsv).join(","),
      ...rows.map((row) => row.map(escapeCsv).join(",")),
    ].join("\n");

    const now = new Date().toISOString().split("T")[0];
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Leads_${now}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilters({
      page_no: 1,
      limit: 30,
      sources: [],
      sort_by: "created_date",
      sort_type: SortType.DESC,
    });
  };

  const handleApply = () => {
    setFilters((prev: LeadsFilters) => ({
      ...prev,
      search_text: searchTerm,
      page_no: 1,
    }));
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      {!userStore.hasPermission([EmployeeRoles.VIEW_LEADS]) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">Leads</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View Leads Details
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}
      {userStore.hasPermission([EmployeeRoles.VIEW_LEADS]) && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">Leads</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View Leads Details
              </CustomTypography>
            </Box>
            {leadStore.leads.length > 0 && (
              <AppButton
                variant="primary"
                label="Download Data"
                onClick={handleDownload}
                customStyles={{ minWidth: 120 }}
                startIcon={<DownloadIcon />}
              />
            )}
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isSearchExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">Search</CustomTypography>
              <IconButton size="small">
                {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
              <Grid2 container spacing={2} columns={12} padding={2}>
                <Grid2 size={{ xs: 12, sm: 9, md: 11, lg: 11 }}>
                  <CustomTextField
                    width={"100%"}
                    placeholder="Search by lead ID, Kylas ID, Name"
                    value={searchTerm}
                    onChange={(e) => {
                      handleSearchBar(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleKeyDown(e);
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 3, md: 1, lg: 1 }}>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearchSubmit}
                    customStyles={{ width: "100%" }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <AppSearchDropdownMulti
                    title="Source"
                    isMultiSelect={true}
                    value={filters.sources || []}
                    list={
                      leadStore.sources
                        ? leadStore.sources.map((source) => ({
                            id: String(source.id),
                            value: source.name,
                          }))
                        : ([] as FilterValue[])
                    }
                    local={false}
                    handleSelect={(items: FilterValue[]) => {
                      setFilters((prev: LeadsFilters) => ({
                        ...prev,
                        sources: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear"
                      onClick={resetFilters}
                      customStyles={{ width: "100%" }}
                    />
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApply}
                      customStyles={{ width: "100%" }}
                    />
                  </Box>
                </Grid2>
              </Grid2>
            </Collapse>
          </Paper>

          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              {leadStore.leads.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10rem"
                  textAlign="center"
                  border="1px dashed"
                  borderColor="divider"
                  borderRadius={2}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={500}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No leads match the current search criteria.
                  </CustomTypography>
                </Box>
              )}
              {leadStore.leads.length > 0 && (
                <LeadTable
                  maxHeight={window.innerHeight - 350}
                  leads={leadStore.leads}
                  limit={leadStore.limit}
                  page_no={filters.page_no}
                  total_count={leadStore.total_count}
                  total_pages={leadStore.total_pages}
                  sortBy={filters.sort_by}
                  sortType={filters.sort_type}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={(column, sortType) => {
                    setFilters((prev: LeadsFilters) => ({
                      ...prev,
                      sort_by: column,
                      sort_type: sortType,
                    }));
                  }}
                  onViewDetails={handleViewDetails}
                />
              )}
            </Box>
          )}
        </Paper>
      )}
      <LeadDetailsPopup
        open={leadStore.isDetailsPopupOpen}
        lead={leadStore.selectedLeadDetails}
        onClose={handleCloseDetails}
      />
    </Grid2>
  );
};

export default observer(LeadsListing);
